// AUTO-GENERATED from the Owner Criteria schema (tblc8Ckf8rR5aNKGP). Option strings match
// Airtable choices EXACTLY (do not hand-edit option text). Labels/intro copy are editorial.
// Source of truth for the client onboarding questionnaire (journey module + OPC report).

export const ONBOARDING_QUESTIONNAIRE = {
  "intro": {
    "title": "Pre-design Questionnaire",
    "paragraphs": [
      "This questionnaire is designed to help us understand your goals, preferences, and priorities for the project. Your responses will guide early design decisions and help ensure the final outcome supports how you live, work, and experience your home.",
      "It is divided into seven parts, each focusing on a different aspect of your project.",
      "All selection questions are required, but each includes an option to indicate it doesn't apply or that you'd like to describe your situation differently. The optional text fields are there for additional thoughts, examples, or context — use them however you wish.",
      "The purpose of this process is not to make final design choices, but to begin a shared conversation that clarifies what matters most and sets the foundation for a thoughtful design approach."
    ]
  },
  "steps": [
    {
      "key": "drivers",
      "title": "Project Drivers",
      "intro": "Every project starts with a reason. This section helps us understand what's driving yours, what you want to achieve, and how you like to work.",
      "questions": [
        {
          "key": "primary_goal",
          "kind": "single",
          "label": "This project is primarily about…",
          "options": [
            "A retreat for family life",
            "A setting for entertaining",
            "A multi-generational home",
            "A seasonal or second home",
            "A generational legacy home",
            "A design-forward home",
            "A private, low-profile home",
            "An investment or resale property",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "project_vision",
          "kind": "longtext",
          "label": "If this project is a success, what has changed about how you live in your home?",
          "required": true
        },
        {
          "key": "program",
          "kind": "longtext",
          "label": "Tell us about the spaces in your home — what's working, what isn't, and what's missing?",
          "required": true
        },
        {
          "key": "key_adjacencies",
          "kind": "longtext",
          "label": "Do you have preferences about how spaces relate to each other?",
          "required": false
        },
        {
          "key": "leading_driver",
          "kind": "single",
          "label": "If a difficult situation required a trade-off, which would you protect first — your budget, your schedule, or the quality of the work?",
          "options": [
            "Quality",
            "Budget",
            "Schedule"
          ],
          "required": true
        },
        {
          "key": "secondary_driver",
          "kind": "single",
          "label": "And which would come next?",
          "options": [
            "Quality",
            "Budget",
            "Schedule"
          ],
          "required": true
        },
        {
          "key": "collaboration_notes",
          "kind": "longtext",
          "label": "When it comes to working with an architect, what do you value most — the experience of the creative process, or having an expert handle it so you don't have to?",
          "required": true
        }
      ]
    },
    {
      "key": "lifestyle",
      "title": "Lifestyle & Use",
      "intro": "How you live day to day is just as important as what you want to build. This section helps us understand your routines, priorities, and the way you use your home, so our work is directed at a result that fits your life today and the life you're working toward.",
      "questions": [
        {
          "key": "pace_of_living",
          "kind": "single",
          "label": "How would you describe your desired pace of living at home?",
          "options": [
            "Relaxed and restorative",
            "High-energy and dynamic",
            "Flexible balance between active and quiet zones",
            "No specific preference",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "entertainment_lifestyle",
          "kind": "multi",
          "label": "How do you like to entertain at home?",
          "options": [
            "Large indoor gatherings",
            "Intimate dinner parties",
            "Outdoor entertaining",
            "Casual or informal get-togethers",
            "Dedicated media or game room",
            "No special entertainment needs",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "daily_living_priorities",
          "kind": "multi",
          "label": "What daily living priorities should the design support?",
          "options": [
            "Work-from-home or remote work",
            "Aging in place",
            "Child-friendly environment",
            "Pet accommodations",
            "Fitness and wellness",
            "Home-based caregiving",
            "Homeschooling or remote learning",
            "No specific daily living priorities",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "interaction_and_retreat",
          "kind": "single",
          "label": "How do you prefer to balance shared and private spaces in your home?",
          "options": [
            "Spaces for gathering are prioritized, with fewer enclosed retreats",
            "A clear balance between shared and private spaces is desired",
            "Private, quiet zones are a strong priority",
            "No specific preference",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "storage_needs",
          "kind": "multi",
          "label": "Do you have any specific storage or utility needs?",
          "options": [
            "Large pantry or kitchen storage",
            "Garage / gear storage",
            "Mudroom or drop zone",
            "Laundry and linen organization",
            "Home office",
            "No special storage needs",
            "Other"
          ],
          "notes": true,
          "required": true
        }
      ]
    },
    {
      "key": "cultural",
      "title": "Cultural",
      "intro": "A home should feel like it belongs to the people who live in it. These next questions explore the aesthetics, traditions, and personal sensibilities that matter to you, so our work is directed at creating something that feels genuinely yours from the inside out.",
      "questions": [
        {
          "key": "Client Values",
          "kind": "multi",
          "label": "What do you want your home to stand for?",
          "options": [
            "Simplicity",
            "Functionality",
            "Efficiency",
            "Flow",
            "Durability",
            "Craftsmanship",
            "Handcrafted Elements",
            "Timelessness",
            "Authenticity",
            "Restraint",
            "Boldness",
            "Heritage",
            "Innovation",
            "Sustainability",
            "Privacy",
            "Family Life",
            "Indoor / Outdoor Living",
            "Connection to Nature",
            "Flexibility",
            "Inspriation"
          ],
          "limit": 5,
          "required": true
        },
        {
          "key": "Emotional Tone",
          "kind": "multi",
          "label": "How do you want your home to feel?",
          "options": [
            "Calm",
            "Peaceful",
            "Serene",
            "Quiet",
            "Grounded",
            "Centered",
            "Balanced",
            "Harmonious",
            "Warm",
            "Cozy",
            "Inviting",
            "Intimate",
            "Retreat",
            "Enveloped",
            "Soft",
            "Airy",
            "Light-filled",
            "Spacious",
            "Uplifting",
            "Joyful",
            "Playful",
            "Energizing",
            "Reflective",
            "Moody",
            "Raw",
            "Minimal"
          ],
          "limit": 5,
          "required": true
        },
        {
          "key": "aesthetic_vision",
          "kind": "single",
          "label": "How would the Owner describe their overall aesthetic vision for the project? (Choose one, leave blank if not applicable)",
          "options": [
            "Modern / Contemporary",
            "Traditional / Classic",
            "Transitional (blend of modern & traditional)",
            "Rustic / Natural",
            "Minimalist",
            "Industrial",
            "Eclectic",
            "No defined aesthetic vision",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "color_palette",
          "kind": "single",
          "label": "Which color palette appeals to you most?",
          "options": [
            "Light and airy",
            "Neutral and subtle",
            "Bold and dramatic",
            "Dark and moody",
            "No specific preference",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "material_palette",
          "kind": "single",
          "label": "Which material character appeals to you most?",
          "options": [
            "Smooth and refined",
            "Raw and textural",
            "Warm and organic",
            "Rich and layered",
            "Light and minimal",
            "No specific preference",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "cultural_design_influences",
          "kind": "multi",
          "label": "Which cultural or regional design influences, if any, should be reflected in the project?",
          "options": [
            "No specific preferences",
            "Mediterranean / Spanish Revival",
            "Japanese / Zen-inspired",
            "Scandinavian / Nordic simplicity",
            "Traditional American",
            "Modernist / Bauhaus / Mid-Century influences",
            "Coastal or Tropical design influences",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "cultural_materials_artifacts",
          "kind": "multi",
          "label": "Would you like to incorporate any materials, elements, or artworks with cultural or regional significance?",
          "options": [
            "No specific preferences",
            "Locally sourced or regional materials",
            "Reclaimed or repurposed materials",
            "Art, murals, or cultural installations",
            "Handcrafted or artisanal elements",
            "Symbolic or ceremonial objects",
            "Family or cultural heirlooms",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "spiritual_symbolic_elements",
          "kind": "multi",
          "label": "Would you like the design to incorporate any spiritual, symbolic, or philosophical principles?",
          "options": [
            "No specific preferences",
            "Feng Shui principles",
            "Vastu Shastra",
            "Sacred geometry or proportion-based design",
            "Symbolic or meaning-based spatial organization",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "special_cultural_spaces",
          "kind": "multi",
          "label": "Would you like the design to include any culturally or personally significant gathering or ceremonial spaces?",
          "options": [
            "No specific preferences",
            "Traditional courtyard or central gathering space",
            "Meditation or prayer room",
            "Tea room or contemplation area",
            "Space for family celebrations and rituals",
            "Outdoor altar, shrine, or reflection garden",
            "Other"
          ],
          "notes": true,
          "required": true
        }
      ]
    },
    {
      "key": "site",
      "title": "Site",
      "intro": "Your site is not just a backdrop — it's a participant in the design. This section helps us understand how you relate to your outdoor environment, what you want to protect, and what you want to build upon.",
      "questions": [
        {
          "key": "topography_landform",
          "kind": "single",
          "label": "How would you describe the way the land moves across your site?",
          "options": [
            "Flat",
            "Gentle Slope",
            "Steep",
            "Terraced"
          ],
          "required": true
        },
        {
          "key": "preferred_views",
          "kind": "multi",
          "label": "Are there any views that should be prioritized? (Check all that apply, leave blank if none)",
          "options": [
            "Emphasize canyon or hillside views",
            "Emphasize city or landscape views",
            "Emphasize mountain views",
            "Emphasize ocean views",
            "Emphasize water features",
            "Highlight mature trees or natural features",
            "Preserve views of specific landmarks or points of interest",
            "No specific view priorities",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "sun_orientation",
          "kind": "single",
          "label": "How would you describe your relationship with sunlight at home?",
          "options": [
            "Bright and sun-filled",
            "A balance of light and shade",
            "Cool and shaded",
            "No specific preference",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "outdoor_features",
          "kind": "multi",
          "label": "Which outdoor elements are you hoping to include?",
          "options": [
            "Covered patio or terrace",
            "Outdoor dining area",
            "Outdoor kitchen or barbecue",
            "Fire pit or outdoor fireplace",
            "Pool or spa",
            "Outdoor shower",
            "Water feature or fountain",
            "Shade structure or pergola",
            "Garden planting area",
            "Raised garden beds or vegetable garden",
            "Sport court or putting green",
            "Dog run or pet area",
            "None - primarily interior scope",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "landscaping_scope",
          "kind": "single",
          "label": "How much landscape improvement are you hoping to include in this project?",
          "options": [
            "None — primarily interior scope",
            "Minimal — limited to touch-up or replacement of existing plantings",
            "Moderate — focused updates or refinements to existing layout",
            "Extensive — full landscape redesign or new outdoor program",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "site_preserve",
          "kind": "multi",
          "label": "Which existing site features are intended to be preserved?",
          "options": [
            "Existing structures or walls",
            "Hardscape elements",
            "Mature trees or significant vegetation",
            "Natural features",
            "View corridors",
            "No specific preservation priorities",
            "Other"
          ],
          "notes": true,
          "required": true
        }
      ]
    },
    {
      "key": "electrical",
      "title": "Electrical",
      "intro": "This section helps us plan ahead for your electrical needs so the right infrastructure is in place from the start.",
      "questions": [
        {
          "key": "electrical_amperage_upgrade",
          "kind": "single",
          "label": "Will the electrical service panel be upgraded? (Choose one, leave blank if not applicable)",
          "options": [
            "No upgrade planned",
            "Upgrade to 200-amp service",
            "Upgrade to 400-amp service"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "high_power_appliances",
          "kind": "multi",
          "label": "Are there any new high-power appliances or equipment you're planning to include?",
          "options": [
            "Electrical induction range",
            "Commercial-grade kitchen equipment",
            "Sauna or steam shower",
            "Workshop or home studio equipment",
            "Outdoor kitchen appliances",
            "Large-capacity laundry equipment",
            "Radiant floor heating",
            "Home elevator or lift",
            "Wine cellar cooling system",
            "Home theater or media room equipment",
            "No special high-power appliances planned",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "smart_home_features",
          "kind": "multi",
          "label": "Which smart home features are planned or of interest?",
          "options": [
            "Future-ready infrastructure only",
            "Smart lighting control",
            "Smart locks or access control",
            "Smart thermostats or climate control",
            "Voice-activated controls",
            "Whole-home automation or central hub integration",
            "Integrated security system",
            "No smart home features planned",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "audio_visual",
          "kind": "multi",
          "label": "Are there any audio or video systems you'd like to plan for?",
          "options": [
            "No specific AV needs",
            "In-ceiling or in-wall speaker systems",
            "Whole-home audio distribution",
            "Pre-wiring for future AV needs",
            "Outdoor audio",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "ev_charging",
          "kind": "multi",
          "label": "Are there any EV charging needs for this project?",
          "options": [
            "Not Applicable",
            "Pre-wiring for future use",
            "One or more Level 2 chargers",
            "Integrated with solar battery storage",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "backup_power",
          "kind": "multi",
          "label": "Are there any backup power or energy storage needs for this project?",
          "options": [
            "Not applicable",
            "Pre-wire for future use",
            "Standby generator",
            "Battery storage system",
            "Solar system",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "owner_involvement_outlet_placement",
          "kind": "single",
          "label": "How involved would you like to be in the placement of outlets and switches?",
          "options": [
            "I'd like to be involved in all placement decisions",
            "I'd like to review the plan before it's finalized",
            "I'm comfortable leaving placement to the design team",
            "Other"
          ],
          "notes": true,
          "required": true
        }
      ]
    },
    {
      "key": "sustainability",
      "title": "Sustainability",
      "intro": "Sustainable design is already part of how we work. This section is about how far you'd like to go beyond that — whether you have specific systems, materials, or certifications in mind, or simply want to plan ahead for future possibilities.",
      "questions": [
        {
          "key": "renewable_energy_systems",
          "kind": "multi",
          "label": "Are there any renewable energy systems you'd like to include or plan for?",
          "options": [
            "No renewable energy systems planned",
            "Infrastructure for future renewable systems",
            "Grid-tied solar system",
            "Off-grid solar system",
            "Geothermal heating and cooling",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "water_efficiency_features",
          "kind": "multi",
          "label": "Are there any water efficiency, management, or quality features you'd like to include or plan for?",
          "options": [
            "No specific water features planned",
            "Infrastructure for future water systems",
            "Rainwater collection system",
            "Graywater reuse system",
            "Drought-tolerant or low-water landscaping",
            "Whole-home water filtration",
            "Water softening system",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "sustainable_materials",
          "kind": "multi",
          "label": "Are there any sustainable material priorities you'd like to emphasize?",
          "options": [
            "No specific sustainable material priorities",
            "Reclaimed or salvaged materials",
            "Rapidly renewable materials",
            "Regionally or locally sourced materials",
            "High-recycled-content materials",
            "Low-embodied-carbon materials",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "indoor_air_quality",
          "kind": "multi",
          "label": "Are there any indoor air quality goals you'd like to prioritize?",
          "options": [
            "Standard approach is sufficient",
            "Enhanced air filtration",
            "Energy recovery ventilation",
            "Radon mitigation system",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "certification_goals",
          "kind": "multi",
          "label": "Are there any sustainability certifications or performance goals you'd like to pursue?",
          "options": [
            "No specific certification or performance goals",
            "LEED certification",
            "Passive House certification",
            "Net Zero Energy or Net Zero Ready",
            "WELL Building Standard",
            "Living Building Challenge",
            "Custom performance targets",
            "Other"
          ],
          "notes": true,
          "required": true
        }
      ]
    },
    {
      "key": "fenestration",
      "title": "Fenestration",
      "intro": "Tell us about the scope of window and door work you're anticipating and whether you have any material preferences.",
      "questions": [
        {
          "key": "window_replacement_scope",
          "kind": "single",
          "label": "How would you describe the window and door scope for this project?",
          "options": [
            "No window or door work planned or applicable",
            "Replace all windows and doors",
            "Replace select windows and doors",
            "Install new windows and doors as part of an addition or new build",
            "Retain existing windows and doors with minor repairs or refinishing",
            "Other"
          ],
          "notes": true,
          "required": true
        },
        {
          "key": "window_door_materials",
          "kind": "multi",
          "label": "Do you have any preferences for certain window and door materials?",
          "options": [
            "Not Applicable",
            "No specific preference",
            "Aluminum",
            "Steel",
            "Wood",
            "Clad wood",
            "Composite or fiberglass",
            "Other"
          ],
          "notes": true,
          "required": true
        }
      ]
    }
  ]
}

export const QUESTION_KEYS = ["primary_goal", "project_vision", "program", "key_adjacencies", "leading_driver", "secondary_driver", "collaboration_notes", "pace_of_living", "entertainment_lifestyle", "daily_living_priorities", "interaction_and_retreat", "storage_needs", "Client Values", "Emotional Tone", "aesthetic_vision", "color_palette", "material_palette", "cultural_design_influences", "cultural_materials_artifacts", "spiritual_symbolic_elements", "special_cultural_spaces", "topography_landform", "preferred_views", "sun_orientation", "outdoor_features", "landscaping_scope", "site_preserve", "electrical_amperage_upgrade", "high_power_appliances", "smart_home_features", "audio_visual", "ev_charging", "backup_power", "owner_involvement_outlet_placement", "renewable_energy_systems", "water_efficiency_features", "sustainable_materials", "indoor_air_quality", "certification_goals", "window_replacement_scope", "window_door_materials"]
